/**
 * Project factory + shape constants — Phase D split (Feb 2026).
 *
 * Extracted verbatim from `useStore.js` so the store file shrinks
 * without changing any externally-visible identity. The constants are
 * still re-exported from `useStore.js` (named exports) so existing
 * imports `import useStore, { SHAPE_TYPES } from '@/store/useStore'`
 * keep working byte-identically.
 *
 *   - SHAPE_TYPES               (ONE_WALL | L_SHAPE | U_SHAPE)
 *   - L_SHAPE_DOOR_SIDES        ('left' | 'right')
 *   - U_SHAPE_DOOR_CONFIGS      ('single' | 'double')
 *   - createDefaultProject()    fresh project skeleton w/ Wall A only
 *   - createWallsForShape(...)  3-wall standardized layout per shape
 */
import { v4 as uuidv4 } from 'uuid';

export const SHAPE_TYPES = {
  ONE_WALL: 'ONE_WALL',
  L_SHAPE: 'L_SHAPE',
  U_SHAPE: 'U_SHAPE',
};

// L-Shape door side options
// Determines which two walls are designable and door position
export const L_SHAPE_DOOR_SIDES = {
  LEFT: 'left',   // Walls B + C are design walls, door on LEFT of wall opposite B
  RIGHT: 'right', // Walls A + B are design walls, door on RIGHT of wall opposite B
};

export const U_SHAPE_DOOR_CONFIGS = {
  SINGLE: 'single',   // Current behavior: one door opening (front)
  DOUBLE: 'double',   // Two door openings: front + where Wall B was
};

export const createDefaultProject = () => ({
  id: uuidv4(),
  name: 'Untitled Closet',
  version: 1,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  units: 'inches',
  shapeType: SHAPE_TYPES.ONE_WALL,
  // L-Shape door side configuration (only relevant when shapeType === L_SHAPE)
  // 'left' = design walls are B+C, door on left of entry wall
  // 'right' = design walls are A+B, door on right of entry wall
  lShapeDoorSide: L_SHAPE_DOOR_SIDES.RIGHT,
  room: {
    height: 96,
    defaultDepth: 24,
    panelThickness: 0.625,  // 5/8" default; configurable via Edit Room Settings
    finishes: {
      wallColor: '#f5f5f4',
    },
    doorConfig: {
      type: 'none',
      width: 36,
      position: 'center',
      wallId: null,
    },
  },
  // Global Accessories - Style Settings
  styleSettings: {
    woodColor: 'white',
    customWoodColor: null,
    handleStyle: 'none',
    faceStyle: 'flat', // 'flat' | 'shaker' — applies to door + drawer faces only
  },
  walls: [
    { id: 'wall-a', label: 'A', name: 'Wall A', length: 96, height: null, notes: '', orderIndex: 0 },
  ],
  items: [],
  // Corner buffers for L-shape and U-shape
  // New format: buffersByWall = { 'wall-a': { start: Buffer|null, end: Buffer|null }, ... }
  buffersByWall: {
    'wall-a': { start: null, end: null },
    'wall-b': { start: null, end: null },
    'wall-c': { start: null, end: null },
  },
  selectedBufferId: null, // Track selected buffer for controls
  references: {
    uploadedImages: [],
  },
  lastCartLink: null,
});

export const createWallsForShape = (
  shapeType,
  defaultHeight = null,
  lShapeDoorSide = L_SHAPE_DOOR_SIDES.RIGHT
) => {
  // STANDARDIZED 3-WALL SYSTEM for L-shape and U-shape
  // All walls (A, B, C) always exist with fixed IDs
  // 'designed' flag indicates if the wall is active for design
  //
  // SPATIAL LAYOUT (standing at door, looking in):
  //   Wall A = LEFT side
  //   Wall B = BACK/opposite (always the wall you see straight ahead)
  //   Wall C = RIGHT side

  switch (shapeType) {
    case SHAPE_TYPES.L_SHAPE:
      // L-Shape: Always 3 walls, but one is empty (not designed)
      if (lShapeDoorSide === L_SHAPE_DOOR_SIDES.LEFT) {
        // Door Left: Wall A is empty, Walls B + C are designed
        return [
          { id: 'wall-a', label: 'A', name: 'Wall A', length: 96, height: defaultHeight, notes: '', orderIndex: 0, designed: false },
          { id: 'wall-b', label: 'B', name: 'Wall B', length: 96, height: defaultHeight, notes: '', orderIndex: 1, designed: true },
          { id: 'wall-c', label: 'C', name: 'Wall C', length: 96, height: defaultHeight, notes: '', orderIndex: 2, designed: true },
        ];
      } else {
        // Door Right (default): Walls A + B are designed, Wall C is empty
        return [
          { id: 'wall-a', label: 'A', name: 'Wall A', length: 96, height: defaultHeight, notes: '', orderIndex: 0, designed: true },
          { id: 'wall-b', label: 'B', name: 'Wall B', length: 96, height: defaultHeight, notes: '', orderIndex: 1, designed: true },
          { id: 'wall-c', label: 'C', name: 'Wall C', length: 96, height: defaultHeight, notes: '', orderIndex: 2, designed: false },
        ];
      }
    case SHAPE_TYPES.U_SHAPE:
      // U-Shape: All 3 walls are designed
      return [
        { id: 'wall-a', label: 'A', name: 'Wall A', length: 96, height: defaultHeight, notes: '', orderIndex: 0, designed: true },
        { id: 'wall-b', label: 'B', name: 'Wall B', length: 96, height: defaultHeight, notes: '', orderIndex: 1, designed: true },
        { id: 'wall-c', label: 'C', name: 'Wall C', length: 96, height: defaultHeight, notes: '', orderIndex: 2, designed: true },
      ];
    default:
      // 1-Wall: Only Wall A
      return [
        { id: 'wall-a', label: 'A', name: 'Wall A', length: 96, height: defaultHeight, notes: '', orderIndex: 0, designed: true },
      ];
  }
};
